import { useState } from "react";
import { Mail, Phone, MapPin, Clock, ArrowUpRight, Check, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import SiteLayout from "@/components/site/SiteLayout";
import { BRAND, SERVICES, SERVICE_TOWNS_BY_AREA } from "@/lib/content";
import { submitContact } from "@/lib/api";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  postcode: "",
  service: "",
  message: "",
  consent: false,
  company_website: "", // honeypot — must remain empty
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name.";
    if (!form.email.trim()) e.email = "An email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "That email looks incomplete.";
    if (!form.service) e.service = "Please select a service.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Please add a few words about your enquiry.";
    if (!form.consent) e.consent = "Please confirm you're happy for us to contact you.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        postcode: form.postcode.trim() || null,
        service: form.service,
        message: form.message.trim(),
        company_website: form.company_website, // honeypot field
      });
      setSubmitted(true);
      toast.success("Enquiry received — we'll be in touch shortly.");
      setForm(INITIAL);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Sorry, something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Unable to submit enquiry.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SiteLayout
      pageTitle="Contact"
      pageDescription="Contact Declan Bishop Property Consultant for EPCs, landlord inspections, snagging, condition surveys and more throughout Lincolnshire — Lincoln, Grantham, Sleaford, Boston, Skegness, Spalding, Louth, Bourne, Horncastle and surrounding towns. Quick response, fixed fees, plain-English advice."
      pagePath="/contact"
    >
      <Toaster position="top-center" richColors />

      {/* Header */}
      <section data-testid="contact-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="grid grid-cols-12 gap-y-10 md:gap-x-12">
            <div className="col-span-12 md:col-span-8">
              <div className="overline" data-testid="contact-overline">Get in touch</div>
              <h1
                data-testid="contact-headline"
                className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight"
              >
                Start a conversation
                <br />
                <span className="italic font-light text-brand-champagne">about your property.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
                Tell us a little about the property and the service you need. We&apos;ll come back
                to you with availability, a fixed fee, and a short next-steps note —
                usually within one working day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section data-testid="contact-form-section" className="bg-white">
        <div className="container-narrow py-20 md:py-28 grid grid-cols-12 gap-y-12 md:gap-x-16">
          {/* Form */}
          <div className="col-span-12 lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="space-y-8"
            >
              {/* Honeypot — visually hidden from humans, irresistible to bots */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label htmlFor="company_website">Company Website (leave blank)</label>
                <input
                  id="company_website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company_website}
                  onChange={update("company_website")}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Full Name"
                  htmlFor="name"
                  error={errors.name}
                >
                  <input
                    id="name"
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-input-name"
                    className={inputClass(errors.name)}
                  />
                </Field>
                <Field
                  label="Email"
                  htmlFor="email"
                  error={errors.email}
                >
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-input-email"
                    className={inputClass(errors.email)}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Phone (optional)" htmlFor="phone">
                  <input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-input-phone"
                    className={inputClass()}
                  />
                </Field>
                <Field label="Property Postcode (optional)" htmlFor="postcode">
                  <input
                    id="postcode"
                    type="text"
                    value={form.postcode}
                    onChange={update("postcode")}
                    placeholder="e.g. PE1 1AA"
                    data-testid="contact-input-postcode"
                    className={inputClass()}
                  />
                </Field>
              </div>

              <Field label="Service" htmlFor="service" error={errors.service}>
                <select
                  id="service"
                  value={form.service}
                  onChange={update("service")}
                  data-testid="contact-input-service"
                  className={`${inputClass(errors.service)} appearance-none bg-white pr-10`}
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%230A192F' d='M1 1l5 5 5-5'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 14px center",
                    backgroundSize: "12px 8px",
                  }}
                >
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                  <option value="Other / Not sure">Other / Not sure</option>
                </select>
              </Field>

              <Field label="How can we help?" htmlFor="message" error={errors.message}>
                <textarea
                  id="message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  className={inputClass(errors.message) + " resize-none"}
                  placeholder="A few details about the property and the question you'd like answered."
                />
              </Field>

              <label className="flex items-start gap-3 text-sm text-brand-navy/75 select-none">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={update("consent")}
                  data-testid="contact-input-consent"
                  className="mt-1 h-4 w-4 border-brand-navy/30 text-brand-navy focus:ring-brand-navy"
                />
                <span>
                  I&apos;m happy for Declan Bishop Property Consultant to contact me about this
                  enquiry. We never share your details with third parties — see our{" "}
                  <a
                    href="/privacy"
                    className="underline hover:text-brand-navy"
                    data-testid="contact-privacy-link"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
              {errors.consent && (
                <p data-testid="error-consent" className="text-xs text-red-600 -mt-4">{errors.consent}</p>
              )}

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-3 bg-brand-navy text-brand-cream px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Enquiry <ArrowUpRight size={16} />
                    </>
                  )}
                </button>
                {submitted && (
                  <span
                    data-testid="contact-success"
                    className="inline-flex items-center gap-2 text-sm text-brand-navy/70"
                  >
                    <Check size={16} className="text-brand-champagne" />
                    Thank you — your enquiry has been received.
                  </span>
                )}
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <aside
            data-testid="contact-sidebar"
            className="col-span-12 lg:col-span-5 space-y-px bg-brand-navy/10 border border-brand-navy/10"
          >
            <Info
              icon={<Phone size={18} />}
              label="Phone"
              testid="contact-info-phone"
              value={
                <a href={`tel:${BRAND.phone.replace(/\s+/g, "")}`} className="hover:text-brand-champagne">
                  {BRAND.phone}
                </a>
              }
            />
            <Info
              icon={<Mail size={18} />}
              label="Email"
              testid="contact-info-email"
              value={
                <a href={`mailto:${BRAND.email}`} className="hover:text-brand-champagne break-all">
                  {BRAND.email}
                </a>
              }
            />
            <Info
              icon={<MapPin size={18} />}
              label="Service Area"
              testid="contact-info-area"
              value={
                <span>
                  Throughout <strong>Lincolnshire</strong> &amp; surrounding areas —
                  <br />
                  Lincoln, Grantham, Sleaford, Boston, Skegness, Spalding &amp; more.
                </span>
              }
            />
            <Info
              icon={<Clock size={18} />}
              label="Hours"
              testid="contact-info-hours"
              value={<span>{BRAND.hours}</span>}
            />
          </aside>
        </div>
      </section>

      {/* Service enquiry options */}
      <section data-testid="contact-service-grid" className="bg-brand-cream border-t border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">Enquire by Service</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight max-w-2xl">
            Already know which service you need? Start there.
          </h2>
          <div className="mt-12 border-t border-l border-brand-navy/10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <button
                key={s.slug}
                onClick={() => {
                  setForm((f) => ({ ...f, service: s.title }));
                  document.getElementById("service")?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                data-testid={`contact-service-${s.slug}`}
                className="text-left p-8 border-r border-b border-brand-navy/10 bg-white hover:bg-brand-navy hover:text-brand-cream transition-colors group"
              >
                <div className="font-serif text-2xl text-brand-champagne">{s.number}</div>
                <h3 className="mt-6 font-serif text-xl md:text-2xl leading-tight">{s.title}</h3>
                <div className="mt-6 text-[10px] tracking-[0.22em] uppercase font-semibold opacity-80 inline-flex items-center gap-2">
                  Use in form <ArrowUpRight size={14} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Location info */}
      <section data-testid="contact-location" className="bg-white border-t border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28 grid grid-cols-12 gap-y-12 md:gap-x-12">
          <div className="col-span-12 md:col-span-5">
            <div className="overline">Location</div>
            <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
              Locally based.
              <br />
              <span className="italic font-light text-brand-champagne">Regionally trusted.</span>
            </h2>
            <p className="mt-6 text-base text-brand-navy/70 leading-relaxed max-w-md">
              Declan Bishop Property Consultant works throughout Lincolnshire and the
              surrounding areas — from the Wolds to the Fens, the coast to the county borders.
              Same-week availability on most services.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 border border-brand-navy/10 bg-brand-cream p-10">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
              Towns We Serve
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
              {SERVICE_TOWNS_BY_AREA.map((area) => (
                <CoverageCol key={area.heading} title={area.heading} items={area.towns} />
              ))}
            </div>
            <p className="mt-10 text-xs tracking-[0.18em] uppercase text-brand-navy/55">
              Somewhere else in Lincolnshire? Get in touch — we may still be able to help.
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function inputClass(error) {
  return [
    "w-full border bg-white px-4 py-3 text-[15px] text-brand-navy",
    "placeholder:text-brand-navy/35",
    "focus:outline-none focus:ring-1 transition-colors",
    error
      ? "border-red-400 focus:border-red-500 focus:ring-red-200"
      : "border-brand-navy/20 focus:border-brand-navy focus:ring-brand-navy/15",
  ].join(" ");
}

function Field({ label, htmlFor, error, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block mb-2 text-[11px] tracking-[0.22em] uppercase text-brand-navy/65 font-semibold">
        {label}
      </label>
      {children}
      {error && (
        <p data-testid={`error-${htmlFor}`} className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function Info({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white p-8 flex items-start gap-5">
      <span className="text-brand-champagne mt-1">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-navy/55 font-semibold">
          {label}
        </div>
        <div className="mt-2 text-brand-navy text-base leading-relaxed">{value}</div>
      </div>
    </div>
  );
}

function CoverageCol({ title, items }) {
  return (
    <div>
      <div className="font-serif text-xl text-brand-navy">{title}</div>
      <ul className="mt-3 space-y-1.5 text-sm text-brand-navy/75">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}
